<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from 'vue'
import { IonContent, isPlatform } from '@ionic/vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import CharacterPose from './chat/CharacterPose.vue'
import EndingDialog from './chat/EndingDialog.vue'
import PreviewEndDialog from './createUniverse/dialog/PreviewEndDialog.vue'
import { useChatsStore } from '@/store/chats'
import { useUserStore } from '@/store/user'
import { useAuthStore } from '@/store/auth'
import ChatRoom from '@/components/ChatRoom.vue'
import H5StoryLine from '@/components/H5StoryLine.vue'
import { useKeyboardStore } from '@/store/keyboard'
import type { Chat } from '@/types'
import TouchDetector from '@/components/TouchDetector.vue'
import LoadingComponent from '@/components/LoadingComponent.vue'
import PovTitle from '@/components/PovTitle.vue'
import { useStream } from '@/composables/useStream'
import {
  autoPlayKey,
  chatRoundEndKey,
  isSingleChatKey,
  lastMessagePositionKey,
  messageKey,
  originalTextKey,
  sekaiAudioPlayingKey,
  streamCompleteKey,
} from '@/constant/symbol'
import { usePolling } from '@/composables/usePolling'
import type { ReturnedUniverse } from '@/services/universe'
import { universeService } from '@/services/universe'
import { generateChatFromReturnedUniverse } from '@/utils/generateChatFromUniverse'
import { useToastStore } from '@/store/toast'
import type { CreatedEventModel } from '@/types/universe'
import { useCreateUniverseStore } from '@/store/createUniverseStore'
import { MAX_EVENT_LIMIT } from '@/constant/chatParams'
import { chatService } from '@/services/chat'
import { useGlobalStateStore } from '@/store/globalState'
import BrowserMediaLimitMask from '@/views/BrowserMediaLimitMask.vue'

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
interface Props {
  chat: Chat
  nextChat: Chat | undefined
  isLoading: boolean
  isPreview: boolean
  index?: number
  active: boolean
}

interface Emits {
  (e: 'playBgm'): void
  (e: 'stopBgm'): void
  (e: 'onToggleDecorations', val: boolean): void
  (e: 'onEnded'): void
}

const { openModal } = useAuthStore()
const userStore = useUserStore()
const chatStore = useChatsStore()
const keyboardStore = useKeyboardStore()
const toastStore = useToastStore()
const { logoutChatInput } = storeToRefs(chatStore)
const createUniverseStore = useCreateUniverseStore()
const globalStore = useGlobalStateStore()
const stream = useStream()
const route = useRoute()

const chat = ref<Chat | undefined>(props.chat)
async function updateEvent(event: CreatedEventModel) {
  if (chat.value) {
    // Update the event first
    chat.value?.universe.events.push(event)
    await nextTick()
    // generate the story based on this event
    stream.handleGenerateEventStory(chat.value, props.isPreview)
  }
}

/**
 * message input el ref
 */
const messageInputEl = ref<InstanceType<typeof H5StoryLine> | null>(null)
/**
 * if the chat has been visited
 */
const hasVisited = ref(false)
/**
 * if the chat reach the last round of the last event
 */
const hasEnded = ref(false)
const showPreviewEndDialog = ref(false)
const { pollingData, startPolling, stopPolling }
  = usePolling<ReturnedUniverse>()

/**
 * The height of the message area,
 * will be updated by the callback from MessageInput
 */
const messageInputElHeight = ref<number>(0)

onMounted(() => {
  onSwipedInView()

  // Auto-initialize streaming if media autoplay is enabled
  if (globalStore.canAutoplayMedia && chat.value) {
    stream.streamInitMessages(chat.value)
  }
})

onUnmounted(() => {
  stream.pause()
  stopPolling()
})

watch(
  () => props.active,
  (newVal) => {
    if (chatStore.currentChatId !== chat.value?.chatId) {
      return
    }

    if (!newVal) {
      stream.pause()
      if (!props.isPreview) {
        stopPolling()
      }
    }
    else {
      if (chatStore.currentChatId === chat.value?.chatId) {
        stream.resume()
      }
    }
  },
)

// provide the messages to the children
provide(messageKey, stream.messages)
provide(chatRoundEndKey, stream.chatRoundEnd)
provide(isSingleChatKey, ref(props.nextChat === undefined))
provide(sekaiAudioPlayingKey, stream.audioPlaying)
provide(streamCompleteKey, stream.responseDone)
provide(originalTextKey, stream.originalTexts)
provide(lastMessagePositionKey, stream.lastMessagePosition)
provide(autoPlayKey, stream.autoPlay)
/**
 * current message object index
 * user tap right/left screen to change it
 */
const currentMessageIndex = ref(0)

watch(
  () => stream.messages.value,
  (newMessages) => {
    if (stream.autoPlay.value) {
      currentMessageIndex.value = newMessages.length - 1
    }
  },
)

// watch stream.messages的长度，长度变化时，触发事件
watch(
  () => stream.messages.value.length,
  (newLength) => {
    if (newLength === 0) {
      return
    }
    // TODO: 抽象埋点逻辑
    if (globalStore.availableDeviceInfo) {
      const payload = chatStore.eventPayload(
        chat.value?.universe.universeId.toString() || '',
        chat.value?.chatId || '',
        'message_visit',
        newLength,
      )
      chatService.reportEvent(payload)
    }
  },
)

const isLastMessage = computed(() => {
  return currentMessageIndex.value === stream.messages.value.length - 1
})

watch(
  () => stream.hasError.value,
  (newVal) => {
    if (newVal) {
      toastStore.setToast('Something went wrong, please try to resend message')
      currentMessageIndex.value = stream.messages.value.length - 1
    }
  },
)

/**
 * Sekai background GIF
 */
const currentBgUrl = computed<string>(() => {
  if (chat.value === undefined) {
    return ''
  }
  return chat.value.backgroundImage || ''
})

watch(
  () => chatStore.currentChatId,
  (newChatId) => {
    if (newChatId === chat.value?.chatId) {
      onSwipedInView()
      if (hasVisited.value) {
        stream.resume()
      }
    }
    else {
      stopPolling()
      console.debug(`Pause chat: ${chat.value?.chatId}`)
      stream.pause()
    }
  },
)

async function onSwipedInView() {
  console.debug('SekaiMainPage: onSwipedInView')
  console.debug(`path: ${route.path}`)
  if (route.path.includes('newSekai')) {
    return
  }

  if (chatStore.currentChatId !== chat.value?.chatId || hasVisited.value) {
    return
  }
  pollingSekai()
  hasVisited.value = true
}

// watch the polling data
watch(
  () => pollingData.value,
  (newSekai) => {
    if (newSekai) {
      const validGenerating = checkNewSekaiStatus(newSekai)
      const newChat = generateChatFromReturnedUniverse(newSekai)
      if (validGenerating) {
        // update the chat event and char, also the universe
        if (newChat && chat.value) {
          chat.value.universe = newChat.universe
          chat.value.chars = newChat.chars
          chat.value.userChar = newChat.userChar
        }
      }
      const validInitMessages = validInitMsgForPreview(newChat)
      createUniverseStore.setCanPublish(validInitMessages)
      if (validGenerating && validInitMessages) {
        stopPolling()
      }
    }
  },
)

function checkGeneratingStatus() {
  // Debug log to check chat.value
  console.debug('[checkGeneratingStatus] chat.value:', chat.value)

  // Check if chat.value exists and has the required properties
  if (!chat.value || !chat.value.universe || !chat.value.universe.events) {
    console.debug(
      '[checkGeneratingStatus] chat.value is missing required properties',
    )
    return false
  }

  // check if the events bgUrl has been generated
  const validEvents = chat.value.universe.events.every(
    event => event.gifUrl !== undefined && event.gifUrl !== '',
  )

  // Check if chars exists before accessing it
  if (!chat.value.chars) {
    console.debug('[checkGeneratingStatus] chat.value.chars is undefined')
    return false
  }

  // check if the character has been generated
  const validChar = chat.value.chars
    .filter(char => char.charId)
    .every(char => char.complete || char.pose?.original !== undefined)

  return validEvents && validChar
}

function validInitMsgForPreview(newChat: Chat) {
  // 这个只在preview的时候才会用到
  return (newChat.universe.initMessages?.length || 0) > 0 || !props.isPreview
}

function checkNewSekaiStatus(sekai: ReturnedUniverse) {
  // check if the events bgUrl has been generated
  const validEvents = sekai.events?.every(
    event => event.gifUrl !== undefined,
  )
  // check if the character has been generated
  const chars = sekai.chars || []

  if (sekai.userChar.charId) {
    // 可能不存在user char
    chars.push(sekai.userChar)
  }

  const validChar = chars.every(char => char.complete)

  return validEvents && validChar
}

// Polling to fetch the sekai until events and character are fully generated
function pollingSekai() {
  console.debug('[pollingSekai] Starting...')

  // Check if chat.value exists
  if (!chat.value) {
    console.debug('[pollingSekai] chat.value is undefined')
    return
  }

  const validSekai = checkGeneratingStatus()

  try {
    const validInitMessages = validInitMsgForPreview(chat.value)
    createUniverseStore.setCanPublish(validInitMessages)

    if (validSekai && validInitMessages) {
      console.debug('[pollingSekai] Sekai is valid, stopping polling')
      return
    }

    console.debug('[pollingSekai] Starting polling for universe updates')
    startPolling(
      () =>
        props.isPreview
          ? universeService.getUniverseDraftById(
            chat.value!.universe.universeId,
          )
          : universeService.getUniverseById(chat.value!.universe.universeId),
      5000,
    )
  }
  catch (error) {
    console.error('[pollingSekai] Error:', error)
  }
}

// check if the chat has ended when the stream ended
watch(
  () => stream.chatRoundEnd.value,
  (newChatRoundEnd) => {
    console.debug('SekaiMainPage: Chat Round End')
    if (
      newChatRoundEnd
      && chatStore.currentChatId === chat.value?.chatId
      && userStore.isLoggedIn
    ) {
      const reachMaxEventLimit
        = (chat.value?.universe.events.length || 0) >= MAX_EVENT_LIMIT
      if (props.isPreview) {
        createUniverseStore.setShowPublishGuide(true)
      }
      if (props.isPreview && reachMaxEventLimit) {
        showPreviewEndDialog.value = true
      }
      hasEnded.value = reachMaxEventLimit
    }
  },
)

/**
 * user tap right screen
 */
function nextMessage() {
  if (stream.lockTap.value) {
    // 保证在stream dequeue当前message的所有字符前，不会触发该事件
    return
  }

  if (stream.messages.value.length > currentMessageIndex.value + 1) {
    // 如果现在message缓存中还存在下一条消息
    console.debug(
      `SekaiMainPage: Next Message: ${currentMessageIndex.value}: The message length: ${stream.messages.value.length}`,
    )
    currentMessageIndex.value += 1
  }
  else {
    // 如果当前已经到了最后一条消息

    // 查看是否还需要继续stream
    const completeStream
      = !stream.queue.value || !stream.queue.value?.data.length

    if (completeStream) {
      // chat中的queue已经空了，说明已经stream完成
      stream.chatRoundEnd.value = true
      setTimeout(() => {
        messageInputEl.value?.showScenarioSheet()
      }, 10)
    }
    else {
      // chat中的queue还有数据，我们继续stream，但是不自动播放
      stream.setSkipToNextMsg(true, () => {
        currentMessageIndex.value += 1
      })
      stream.resume(true)
      stream.autoPlay.value = false
    }
  }
}

/**
 * user tap left screen
 */
function prevMessage() {
  console.debug('SekaiMainPage: Prev Message')
  stream.autoPlay.value = false
  if (currentMessageIndex.value > 0) {
    currentMessageIndex.value -= 1
  }
}

function handleTouch(touchSide: 'right' | 'left') {
  console.log('handleTouch', touchSide)
  if (keyboardStore.isTyping) {
    return
  }
  if (touchSide === 'right') {
    nextMessage()
  }
  else if (touchSide === 'left') {
    prevMessage()
  }
  emit('playBgm')
}

function swipeToNextSekai() {
  const swiperEl: any = document.querySelector('.sekai-swiper')
  if (swiperEl) {
    swiperEl.swiper.slideNext()
  }
}

function reset() {
  currentMessageIndex.value = 0
  hasVisited.value = false
  hasEnded.value = false
  stream.reset()
}

defineExpose({
  reset: () => {
    reset()
  },
  pause: stream.pause,
  resume: stream.resume,
  updateBgImg: (img: string) => {
    // 只在预览使用
    if (!props.isPreview) {
      return
    }
    chat.value!.universe.events[0].gifUrl = img
  },
})
</script>

<template>
  <ion-content
    :id="`chat_content_${chat.chatId}`"
    :style="{
      '--background': `url('${currentBgUrl}') no-repeat center center / cover`,
    }"
    :scroll-y="false"
  >
    <touch-detector
      v-if="!keyboardStore.isTyping"
      :key="`touch-detector-${chat.chatId}`"
      :bottom-offset="messageInputElHeight"
      @tap-detected="handleTouch"
    >
      <div
        :class="`w-full h-full pt-8 pb-4 px-1.5 ${
          isLoading && 'backdrop-blur-lg blur-lg'
        }`"
      >
        <character-pose
          :key="`character-pose-${chat.chatId}`"
          :positions="stream.avatarPositions.value"
          :current-index="currentMessageIndex"
        />
        <chat-room
          :key="`chat-room-${chat.chatId}`"
          :chat="chat"
          :message="stream.messages.value[currentMessageIndex]"
          :height="messageInputElHeight"
          :is-logged-in="userStore.isLoggedIn"
          :waiting-response="stream.loading.value"
          :error="stream.hasError.value"
          @need-open-auth-modal="
            () => {
              openModal();
            }
          "
        />
        <pov-title
          v-if="
            !props.isPreview
              && chat.universe.highlight != null
              && chat.universe.highlight.text
          "
          :key="`pov-title-${chat.chatId}`"
          :text="chat.universe.highlight.text"
          :width="chat.universe.highlight.width"
          :height="chat.universe.highlight.height"
          :x="chat.universe.highlight.x"
          :y="chat.universe.highlight.y"
        />
      </div>
    </touch-detector>
    <loading-component v-if="isLoading" />
    <h5-story-line
      :key="`message-input-${chat.chatId}`"
      ref="messageInputEl"
      :chat="chat"
      :messages="stream.messages.value"
      class="fixed bottom-0 w-full"
      invite-is-hidden
      :disabled="stream.loading.value"
      :chat-round-end="stream.chatRoundEnd.value && isLastMessage"
      :is-preview="isPreview"
      @update-event="updateEvent"
      @send-message="
        (msg) => {
          stream.autoPlay.value = true;
          if (userStore.isLoggedIn) {
            //make sure the chat store update the current chat id
            chatStore.setCurrentChatId(chat.chatId);
            stream.handleSendMessage(msg, chat, isPreview);
          }
          else {
            logoutChatInput = msg;
            // openModal();
          }
        }
      "
      @update-input-height="
        (height) => {
          if (isPlatform('mobileweb')) {
            messageInputElHeight = 220;
          }
          else {
            messageInputElHeight = height;
          }
        }
      "
      @on-toggle-decorations="
        (val) => {
          emit('onToggleDecorations', val);
        }
      "
    />
    <preview-end-dialog
      :is-open="showPreviewEndDialog"
      @on-publish="
        () => {
          createUniverseStore.nextStep();
          showPreviewEndDialog = false;
        }
      "
      @on-replay="
        async () => {
          // reset the chat
          reset();
          showPreviewEndDialog = false;
          await universeService.replayPreviewSekai(chat.universe.universeId);
          // 移除chat的universe里面除了第一个event以外的events
          chat.universe.events = chat.universe.events.slice(0, 1);
          stream.reset();
        }
      "
    />
    <browser-media-limit-mask
      v-if="!globalStore.canAutoplayMedia"
      @play-sound="
        () => {
          emit('playBgm');
          stream.streamInitMessages(chat)
        }
      "
    />
  </ion-content>
  <ending-dialog
    v-if="hasEnded && !isPreview"
    :is-open="hasEnded"
    :chat="chat"
    :next-chat="nextChat"
    @close="() => (hasEnded = false)"
    @next="
      () => {
        swipeToNextSekai();
        hasEnded = false;
      }
    "
  />
</template>

<style scoped>
.response-wrapper {
  background: var(--black-trans-black-24, rgba(0, 0, 0, 0.24));
}
</style>
